import { ChangeDetectorRef, Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { BehaviorSubject, filter, map, Observable, Subject, switchMap, take, takeUntil } from 'rxjs';
import { dispatchDeletedItemEvent, dispatchSavedItemEvent, SearchCriteria, SearchResult } from '@snelstart/domain-logic';
import { SharedUiTableModule, TableConfiguration, ZeroStateConfiguration } from '@snelstart/ui-table';
import {
    BokkzAlgDataService,
    BokkzJrDataService,
    BokkzJrLogic,
    BokkzRapportageClient,
    ClientEntity,
    FeatureServiceComponent,
    JaarrapportageEntity,
    MenuInterface,
} from '@snelstart-ventures/fiscaal/domain-logic';
import { ActivatedRoute, Router } from '@angular/router';
import { FormBuilder } from '@angular/forms';
import { CommonModule, DatePipe } from '@angular/common';
import { ButtonConfiguration, SharedUiButtonModule } from '@snelstart/ui-button';

@Component({
    selector: 'snelstart-venture-fiscaal-feature-tax-report-list',
    templateUrl: './feature-tax-report-list.component.html',
    styleUrls: ['./feature-tax-report-list.component.scss'],
    imports: [SharedUiTableModule, DatePipe, SharedUiButtonModule, CommonModule, FeatureServiceComponent],
})
export class FeatureTaxReportListComponent implements OnInit, OnDestroy {
    @Output() public details = new EventEmitter<string>();
    protected id: any;
    private destroy$ = new Subject<void>();
    protected tableConfig!: TableConfiguration<any, any>;
    public dialogVisible = false;
    public showTable = false;
    public year = {} as number;
    public options$ = new BehaviorSubject<MenuInterface[]>([]);
    public options = [] as MenuInterface[];
    protected taxId: any;
    protected clientId: any;
    public jaarrekeningEntity = {} as JaarrapportageEntity;
    public client = {} as ClientEntity;
    public name: string | undefined = {} as string;
    public isRechtspersoon = false;
    public heeftAandelen = false;
    public rapportageDatum?: Date;

    constructor(
        public dataService: BokkzJrDataService,
        public jrLogic: BokkzJrLogic,
        private algDataService: BokkzAlgDataService,
        private bokkzClient: BokkzRapportageClient,
        private cd: ChangeDetectorRef,
        private router: Router,
        public fb: FormBuilder,
        private activatedRoute: ActivatedRoute,
    ) {}

    ngOnInit(): void {
        this.algDataService.selectedYear.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.year = value;
            this.showTable = false;
            setTimeout(() => {
                this.createTableConfig();
                this.showTable = true;
            }, 0);
        });
        this.activatedRoute.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
            this.taxId = params['reportId'];
            this.clientId = params['clientId'];
        });
        this.algDataService.client.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.client = value;
            this.name = value.name;
        });
        this.dataService.jaarrapportageEntity.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.jaarrekeningEntity = value;
            const datum = value.rapportage?.informatieRapportage?.financialReportingPeriodCurrentEndDate?.value;
            if (datum) {
                this.rapportageDatum = new Date(datum);
            } else {
                const currentYear = new Date().getFullYear();
                this.rapportageDatum = new Date(currentYear - 1, 11, 31);
            }
        });
        this.dataService.isRechtspersoon.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.isRechtspersoon = value;
        });
        this.dataService.heeftAandelen.pipe(takeUntil(this.destroy$)).subscribe((value) => {
            this.heeftAandelen = value;
        });
        this.dataService.options.pipe(takeUntil(this.destroy$)).subscribe((menuInterface) => {
            const old = this.options$.value;
            if (JSON.stringify(this.options$.value) != JSON.stringify(menuInterface)) {
                this.options$.next([...menuInterface.reverse()]);
                this.cd.detectChanges();
                for (let i = 0; i < menuInterface.length; i++) {
                    if (JSON.stringify(old[i]) != JSON.stringify(menuInterface[i])) {
                        dispatchSavedItemEvent({
                            identifier: menuInterface[i].value,
                            result: menuInterface[i] as unknown as Record<number, MenuInterface>,
                        });
                        if (!menuInterface[i].visible) {
                            dispatchDeletedItemEvent({
                                identifier: menuInterface[i].value,
                            });
                        }
                    }
                }
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private createTableConfig(): void {
        const criteria = new SearchCriteria();

        this.tableConfig = TableConfiguration.create({
            identifier: 'value',
            options: {
                useSearch: false,
                useSort: true,
            },
            criteria: criteria,

            search: () => {
                return this.bokkzClient.getjaarrapportage(this.clientId, String(this.year)).pipe(
                    switchMap((jaarrekeningenEntity) => {
                        if (jaarrekeningenEntity.rapportage) {
                            this.dataService.updateEntity(jaarrekeningenEntity);
                            this.jrLogic.getCalculation(jaarrekeningenEntity);
                        } else {
                            this.options$.next([]);
                            this.dataService.updateAantalValidaties(0);
                        }
                        return this.options$.pipe(
                            filter((options) => Array.isArray(options)),
                            map((options) => {
                                const sortedGroups = options
                                    .filter((item) => item.visible)
                                    .map((group) => ({
                                        ...group,
                                        subonderdelen: (group.subonderdelen || [])
                                            .filter((sub) => sub.visible)
                                            .sort((a, b) => a.value - b.value),
                                    }))
                                    .sort((a, b) => a.value - b.value); // Sorteer hoofdgroepen ook
                                return new SearchResult(sortedGroups, sortedGroups.length);
                            }),
                            take(1),
                        );
                    }),
                );
            },

            details: (entity) => {
                if (entity) {
                    this.details.next(entity.label);
                }
            },

            fromDetailsMap: (entity) => {
                return entity as unknown as Record<number, MenuInterface>;
            },

            zeroStateConfig: ZeroStateConfiguration.create({
                icon: 'book-open-cover',
                mainText: this.year < 2020 ? 'Module niet beschikbaar' : $localize`Geen jaarrapportage gevonden voor ` + this.year,
                subText:
                    this.year < 2020
                        ? 'De jaarrapportage wordt door SnelStart ondersteund vanaf boekjaar 2020.'
                        : 'Maak een nieuwe rapportage aan en start direct met het inlezen van cijfers.',
                buttonText: this.year < 2020 ? undefined : `Maak nieuwe jaarrapportage`,
                buttonConfig:
                    this.year < 2020
                        ? undefined
                        : ButtonConfiguration.create({
                              onClick: () => {
                                  return new Observable((subscriber) => {
                                      setTimeout(() => {
                                          subscriber.next((this.dialogVisible = true));
                                          subscriber.complete();
                                      }, 50);
                                  });
                              },
                          }),
            }),
        });
    }

    navigateToSubPage(pageMapping: string, index?: number): void {
        const url = `/pm-report/modules/${this.clientId}/${pageMapping}`;
        this.router.navigate([url])
            .then(() => console.log('Navigation complete'));
    }
}
